import type { Ref } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { injectionKey } from '@shared/injection'

// The contract a concierge input hands the takeover so it can decide when to engage and when
// to collapse without reaching into the input's own DOM. The getters run inside the takeover's
// reactive computeds, so each must read reactive state in the input.
export type ConciergeInputReport = {
  active: () => boolean                // the buyer is actively working in this input right now
  empty: () => boolean                 // the input's whole query is empty
  dismissOnOutsideClick: () => boolean // a click off the input should dismiss it right now
  collapseOnEmpty?: () => boolean      // an emptied query should close the takeover (default: stays open)
}

export const CONCIERGE_TAKEOVER_KEY
  = injectionKey<ReturnType<typeof setupConciergeTakeover>>('conciergeTakeover')

// A keystroke marks the takeover "settling" for this long, so a burst of typing finishes
// before the teleport moves the field. A timer, not a value-watch — a type-then-backspace
// writes the same string back, which a value-watch would never fire on, so settling would stick.
const SETTLE_CLEAR_MS = 260

function setupConciergeTakeover (engaged: Ref<boolean>) {
  const report = shallowRef<ConciergeInputReport | null>(null)
  const settling = ref(false)
  const { start: scheduleSettleClear } = useTimeoutFn(
    () => { settling.value = false }, SETTLE_CLEAR_MS, { immediate: false },
  )

  // Opens on the buyer's first entry — a first character, a first tap that fills a detail, a
  // click back into a field that already has content — rather than once the brief is
  // resultable. Not on focus alone, so the page doesn't jump under a click before anything
  // is typed.
  const shouldEngage = computed(() => {
    const input = report.value
    return Boolean(input && input.active() && !input.empty() && !settling.value)
  })

  const dismissOnOutsideClick = computed(() => Boolean(report.value?.dismissOnOutsideClick()))

  const shouldCollapse = computed(() => {
    const input = report.value
    return Boolean(input && input.collapseOnEmpty?.() && input.empty())
  })

  function registerInput (input: ConciergeInputReport) {
    report.value = input
    return () => { if (report.value === input) report.value = null }
  }

  // The active input calls this on every keystroke; see SETTLE_CLEAR_MS.
  function markEditing () {
    settling.value = true
    scheduleSettleClear()
  }

  let openRequested = false

  // An open that doesn't come from the input — the results surface's "See all" on the capped
  // at-rest grid. Otherwise identical to clicking the input: the takeover opens at the top with
  // the input focused.
  function engage () {
    if (engaged.value) return
    openRequested = true
    engaged.value = true
  }

  // Read once by the takeover's `engaged` watcher; an input-driven open leaves it false.
  function consumeOpenRequest () {
    const requested = openRequested
    openRequested = false
    return requested
  }

  return {
    engaged: readonly(engaged),
    shouldEngage,
    shouldCollapse,
    dismissOnOutsideClick,
    registerInput,
    markEditing,
    engage,
    consumeOpenRequest,
  }
}

export function provideConciergeTakeover (engaged: Ref<boolean>) {
  const takeover = setupConciergeTakeover(engaged)
  provide(CONCIERGE_TAKEOVER_KEY, takeover)
  return takeover
}

// Injected by an input. Returns undefined when rendered without a takeover (Storybook, the
// framed landing placement), so callers guard with `?.` and degrade to a bare input.
export function useConciergeTakeover () {
  return inject(CONCIERGE_TAKEOVER_KEY, undefined)
}
